import { BarChart3, ArrowLeft, ArrowRight, BriefcaseBusiness, Target, Users, Workflow, Sparkles, Clock3 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminClients, getBusinessCenterProjects } from "../../lib/api";
import type { ClientRecord, BusinessCenterProjectWorkspace } from "../../lib/types";
import { loadTruevanceWorkspace } from "./TruevanceConnectApi";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import { collectTruevanceActivityFeed } from "./TruevanceCenterActivity";
import { loadTruevanceSalesWorkspace } from "./TruevanceSalesCenterApi";
import { calculateTruevanceMetrics, formatCurrency, formatDisplayDate, formatPercent, type TruevanceStore } from "./TruevanceConnectData";
import type { BusinessSalesWorkspacePayload } from "./TruevanceSalesCenterData";

export function TruevanceAnalyticsCenterPage() {
  const [salesWorkspace, setSalesWorkspace] = useState<BusinessSalesWorkspacePayload | null>(null);
  const [truevanceWorkspace, setTruevanceWorkspace] = useState<{ store: TruevanceStore } | null>(null);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [projects, setProjects] = useState<BusinessCenterProjectWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useTruevanceBranding(
    "Analytics Center",
    "Executive reporting for consulting leads, opportunities, client counts, projects, revenue, and follow-up health.",
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      loadTruevanceSalesWorkspace(),
      loadTruevanceWorkspace(),
      getAdminClients({ brand: "truevance_connect" }),
      getBusinessCenterProjects({ brand: "truevance_connect" }),
    ])
      .then(([sales, consulting, brandClients, brandProjects]) => {
        if (cancelled) return;
        setSalesWorkspace(sales);
        setTruevanceWorkspace(consulting);
        setClients(brandClients);
        setProjects(brandProjects);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load analytics data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const consultingMetrics = useMemo(() => (truevanceWorkspace ? calculateTruevanceMetrics(truevanceWorkspace.store) : null), [truevanceWorkspace]);
  const activityFeed = useMemo(
    () =>
      collectTruevanceActivityFeed({
        salesWorkspace,
        truevanceStore: truevanceWorkspace?.store || null,
      }),
    [salesWorkspace, truevanceWorkspace],
  );

  const brandProjects = projects?.projects || [];
  const salesMetrics = salesWorkspace?.metrics || null;

  const topMetrics = [
    { label: "Leads", value: salesMetrics?.leadCount || 0, note: "Total sales intake records." },
    { label: "Discovery", value: salesMetrics?.discoveryScheduledCount || 0, note: "Scheduled discovery calls." },
    { label: "Opportunities", value: salesMetrics?.openOpportunityCount || 0, note: "Active pipeline records." },
    { label: "Converted Clients", value: salesMetrics?.convertedClientCount || 0, note: "Opportunities that reached client conversion." },
    { label: "Brand Clients", value: clients.length, note: "Truevance-scoped client profiles." },
    { label: "Brand Projects", value: brandProjects.length, note: "Live consulting delivery records." },
    { label: "Consulting Revenue", value: formatCurrency(consultingMetrics?.consultingRevenue || 0), note: "Revenue across project delivery." },
    { label: "Pipeline Value", value: formatCurrency(salesMetrics?.pipelineValue || 0), note: "Weighted opportunity value." },
  ];

  const stageBreakdown = [
    { label: "Lead Intake", value: salesWorkspace?.leads.length || 0 },
    { label: "Discovery Calls", value: salesWorkspace?.discoveryCalls.length || 0 },
    { label: "Opportunities", value: salesWorkspace?.opportunities.length || 0 },
    { label: "Follow-Ups", value: salesWorkspace?.followUps.length || 0 },
    { label: "Assessments", value: consultingMetrics?.activeAssessments || 0 },
    { label: "Projects", value: consultingMetrics?.activeProjects || 0 },
  ];

  if (loading) {
    return (
      <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
        <TruevanceBrandHeader />
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="truevance-business-card truevance-business-card--dark mx-auto grid max-w-5xl gap-4 rounded-3xl p-8 sm:p-10">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">{TRUEVANCE_BRAND_NAME}</p>
            <h1 className="font-display text-4xl font-bold text-[#F5F7FA]">Analytics Center</h1>
            <p className="text-sm font-semibold leading-8 text-[#E8EEF4]">Loading consulting, client, project, and follow-up metrics.</p>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="rounded-2xl border border-[rgba(34,224,208,.12)] bg-white/5 p-4">
                  <div className="h-3 w-24 rounded-full bg-[rgba(34,224,208,.18)]" />
                  <div className="mt-4 h-8 w-20 rounded-full bg-[rgba(34,224,208,.12)]" />
                  <div className="mt-4 h-3 w-full rounded-full bg-[rgba(34,224,208,.08)]" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
        <TruevanceBrandHeader />
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="truevance-business-card truevance-business-card--dark mx-auto max-w-4xl rounded-3xl p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">{TRUEVANCE_BRAND_NAME}</p>
            <h1 className="mt-2 font-display text-4xl font-bold text-[#F5F7FA]">Analytics Center</h1>
            <p className="mt-4 text-sm font-semibold leading-8 text-[#E8EEF4]">{error}</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
      <TruevanceBrandHeader />

      <section className="truevance-business-hero border-b border-[rgba(34,224,208,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.25fr_.75fr] lg:items-end">
          <div className="grid gap-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] bg-[rgba(7,26,47,.55)] px-3 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">
              <BarChart3 className="size-4" aria-hidden="true" />
              {TRUEVANCE_BRAND_NAME}
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Analytics Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]">
              Monitor lead intake, discovery progress, consulting conversion, client counts, project delivery, and the current follow-up load from one executive view.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/truevance-business-center" className="admin-secondary-button focus-ring">
                <ArrowLeft className="size-4" aria-hidden="true" />
                Business Center Home
              </Link>
              <Link to="/admin/executive-dashboard" className="admin-secondary-button focus-ring">
                <ArrowRight className="size-4" aria-hidden="true" />
                Executive Dashboard
              </Link>
            </div>
          </div>

          <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Executive summary</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F5F7FA]">Consulting performance at a glance</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">
              The public site attracts clients. This dashboard tracks how the consulting operation is performing.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <MiniMetric label="Win Rate" value={formatPercent(salesMetrics?.winRate || 0)} note="Closed opportunities won." />
              <MiniMetric label="Project Completion" value={formatPercent(consultingMetrics?.projectCompletionRate || 0)} note="Average completion across projects." />
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {topMetrics.map((metric) => (
              <article key={metric.label} className="truevance-business-card truevance-business-card--light rounded-2xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">{metric.label}</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#071A2F]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-7 text-[#334155]">{metric.note}</p>
              </article>
            ))}
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.08fr_.92fr]">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Pipeline and delivery stages" subtitle="Consulting volume across the sales and delivery lifecycle." />
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {stageBreakdown.map((stage) => (
                  <div key={stage.label} className="rounded-2xl border border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.92)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#17D4C4]">{stage.label}</p>
                    <p className="mt-2 font-display text-3xl font-bold text-[#071A2F]">{stage.value}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Operational signals" subtitle="The next metrics that matter for the consulting command center." dark />
              <div className="mt-5 grid gap-3">
                <SignalRow label="Open follow-ups" value={salesMetrics?.followUpCount || 0} note="Current follow-up load." />
                <SignalRow label="Due follow-ups" value={salesMetrics?.followUpsDueCount || 0} note="Items due today or earlier." />
                <SignalRow label="Brand clients" value={clients.length} note="Truevance-scoped client records." />
                <SignalRow label="Brand projects" value={brandProjects.length} note="Live delivery records in the project center." />
                <SignalRow label="Consulting revenue" value={formatCurrency(consultingMetrics?.consultingRevenue || 0)} note="Tracked across client projects." />
              </div>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-[.92fr_1.08fr]">
            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Recent activity" subtitle="Latest operational events from sales, consulting, client, and project workspaces." dark />
              <div className="mt-5 grid gap-3">
                {activityFeed.slice(0, 10).map((item) => (
                  <div key={`${item.source}-${item.id}`} className="rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{item.source}</p>
                        <h3 className="mt-1 font-display text-2xl font-bold text-[#F5F7FA]">{item.title}</h3>
                      </div>
                      <span className="rounded-full border border-[rgba(214,178,74,.22)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#E0C96A]">
                        {formatDisplayDate(item.date)}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.detail}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Planning view" subtitle="How the current workflow signal translates into executive reporting." />
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <MiniMetric label="Open opportunities" value={salesMetrics?.openOpportunityCount || 0} note="Active Truevance pipeline." light />
                <MiniMetric label="Active assessments" value={consultingMetrics?.activeAssessments || 0} note="Assessments currently in motion." light />
                <MiniMetric label="Active projects" value={consultingMetrics?.activeProjects || 0} note="Projects in delivery." light />
                <MiniMetric label="Pipeline value" value={formatCurrency(salesMetrics?.pipelineValue || 0)} note="Weighted opportunity value." light />
              </div>
              <div className="mt-6 rounded-2xl border border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.08)] p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#17D4C4]">Business Center OS bridge</p>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#334155]">
                  This analytics surface is ready to inform future ClientFlow Studio reporting, brand separation, and shared operational dashboards.
                </p>
              </div>
            </article>
          </section>
        </div>
      </section>
    </main>
  );
}

function SectionHeading({ title, subtitle, dark = false }: { title: string; subtitle: string; dark?: boolean }) {
  return (
    <div className={`border-b pb-4 ${dark ? "border-[rgba(34,224,208,.16)]" : "border-[rgba(7,26,47,.08)]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{TRUEVANCE_BRAND_NAME}</p>
      <h2 className={`mt-1 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{subtitle}</p>
    </div>
  );
}

function MiniMetric({ label, value, note, light = false }: { label: string; value: string | number; note: string; light?: boolean }) {
  return (
    <div className={`rounded-2xl border p-4 ${light ? "border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.92)]" : "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.16em] ${light ? "text-[#17D4C4]" : "text-[#22E0D0]"}`}>{label}</p>
      <p className={`mt-2 font-display text-2xl font-bold ${light ? "text-[#071A2F]" : "text-[#F5F7FA]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${light ? "text-[#334155]" : "text-[#E8EEF4]"}`}>{note}</p>
    </div>
  );
}

function SignalRow({ label, value, note }: { label: string; value: string | number; note: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{label}</p>
          <p className="mt-1 font-display text-2xl font-bold text-[#F5F7FA]">{value}</p>
        </div>
        <Clock3 className="size-4 text-[#E0C96A]" aria-hidden="true" />
      </div>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{note}</p>
    </div>
  );
}

